import React, { useCallback, useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Text, Flex } from '@chakra-ui/react';
import { AddIcon, MinusIcon, SearchIcon, CloseIcon } from '@chakra-ui/icons';
import StorageManager from '../../utils/StorageManager';

/**
 * ZoomControls - Floating zoom in/out controls for users who may not be familiar with keyboard shortcuts
 * Provides an intuitive way to increase or decrease the page zoom level
 * 
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the zoom controls are visible
 * @param {function} props.onClose - Function to hide the zoom controls
 */
const ZoomControls = ({ isVisible = false, onClose }) => {
  // Track current zoom level (starting at 100%)
  const [zoomLevel, setZoomLevel] = useState(100);
  
  // Get zoom level on component mount
  useEffect(() => {
    // Try to get stored zoom level from StorageManager
    const savedZoom = StorageManager.get('burrow-app-zoom-level');
    if (savedZoom) {
      const parsedZoom = parseInt(savedZoom, 10);
      setZoomLevel(parsedZoom);
      applyZoom(parsedZoom);
    }
  }, []);
  
  // Apply zoom to document and save to StorageManager
  const applyZoom = useCallback((level) => {
    const zoomValue = level / 100;
    document.body.style.zoom = zoomValue;
    document.documentElement.style.setProperty('--app-zoom', zoomValue);
    StorageManager.set('burrow-app-zoom-level', level.toString());
  }, []);

  // Function to zoom in (increase text size)
  const handleZoomIn = useCallback(() => {
    if (zoomLevel < 200) { // Don't allow over 200%
      const newLevel = zoomLevel + 10;
      setZoomLevel(newLevel);
      applyZoom(newLevel);
    }
  }, [zoomLevel, applyZoom]);

  // Function to zoom out (decrease text size)
  const handleZoomOut = useCallback(() => {
    if (zoomLevel > 60) { // Don't allow below 60%
      const newLevel = zoomLevel - 10;
      setZoomLevel(newLevel);
      applyZoom(newLevel);
    }
  }, [zoomLevel, applyZoom]);
  
  // Function to reset zoom to 100%
  const handleResetZoom = useCallback(() => {
    setZoomLevel(100);
    applyZoom(100);
  }, [applyZoom]);

  // Don't render anything if not visible
  if (!isVisible) return null;

  return (
    <Box
      position="fixed"
      bottom="80px"
      right="20px"
      zIndex={1000}
      display="flex"
      flexDirection="column"
      gap={2}
      bg="rgba(0, 0, 0, 0.5)"
      p={2}
      borderRadius="lg"
      boxShadow="lg"
      backdropFilter="blur(5px)"
    >
      <Tooltip label="Close zoom controls" placement="left" hasArrow>
        <IconButton
          size="xs"
          icon={<CloseIcon />}
          onClick={onClose}
          position="absolute"
          top="-10px"
          right="-10px"
          borderRadius="full"
          colorScheme="red"
        />
      </Tooltip>
      
      <Tooltip label="Make text larger (Zoom In)" placement="left" hasArrow>
        <IconButton
          aria-label="Zoom In"
          icon={<AddIcon boxSize="16px" />}
          onClick={handleZoomIn}
          colorScheme="teal"
          size="md"
          borderRadius="full"
          boxShadow="md"
          isDisabled={zoomLevel >= 200}
        />
      </Tooltip>
      
      <Flex 
        justifyContent="center" 
        alignItems="center" 
        p={1}
        onClick={handleResetZoom} 
        cursor="pointer"
        _hover={{ bg: "rgba(255,255,255,0.1)" }}
        borderRadius="md"
      >
        <Tooltip label="Reset zoom to 100%" placement="left" hasArrow>
          <Text fontSize="xs" fontWeight="bold" color="white">
            {zoomLevel}%
          </Text>
        </Tooltip>
      </Flex>
      
      <Tooltip label="Make text smaller (Zoom Out)" placement="left" hasArrow>
        <IconButton
          aria-label="Zoom Out"
          icon={<MinusIcon boxSize="16px" />}
          onClick={handleZoomOut}
          colorScheme="teal"
          size="md"
          borderRadius="full"
          boxShadow="md"
          isDisabled={zoomLevel <= 60}
        />
      </Tooltip>
    </Box>
  );
};

export default ZoomControls;
